import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { X } from "lucide-react";

/**
 * Inline product editor. Admin can edit all fields; sellers can edit
 * name, price, description, images, sizes, colors, stock, tags.
 * Props:
 *   product: current product
 *   isAdmin: bool - admin sees category + is_custom fields
 *   onSaved: (updatedProduct) => void
 *   onCancel: () => void
 */
export default function ProductEditForm({ product, isAdmin = false, onSaved, onCancel }) {
  const [f, setF] = useState({
    name: product.name || "",
    price: product.price ?? 0,
    stock: product.stock ?? 0,
    description: product.description || "",
    sizes: (product.sizes || []).join(", "),
    colors: (product.colors || []).join(", "),
    tags: (product.tags || []).join(", "),
    images: product.images || [],
    category: product.category || "thrifted",
    is_custom: product.is_custom ?? false,
    bargain_enabled: product.bargain_enabled !== false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.images.length === 0) { toast.error("At least one photo required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        price: Number(f.price),
        stock: Number(f.stock),
        description: f.description.trim(),
        sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colors: f.colors.split(",").map((s) => s.trim()).filter(Boolean),
        tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
        images: f.images,
        bargain_enabled: f.bargain_enabled,
      };
      if (isAdmin) {
        payload.category = f.category;
        payload.is_custom = f.is_custom;
      }
      const { data } = await api.patch(`/products/${product.product_id}`, payload);
      toast.success("Product updated");
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border-2 border-black brutal-shadow p-5 space-y-4 relative" data-testid={`edit-form-${product.product_id}`}>
      <button type="button" onClick={onCancel} data-testid="edit-cancel" className="absolute top-3 right-3 border-2 border-black bg-white w-8 h-8 flex items-center justify-center hover-nudge"><X className="w-4 h-4" /></button>
      <p className="sticker">EDIT · {product.product_id.slice(0, 12)}</p>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs font-mono-b font-bold uppercase">NAME</label>
          <input data-testid="edit-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="brutal-input w-full mt-1" />
        </div>
        <div>
          <label className="text-xs font-mono-b font-bold uppercase">PRICE (₹)</label>
          <input data-testid="edit-price" required type="number" min={0} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="brutal-input w-full mt-1" />
        </div>
        <div>
          <label className="text-xs font-mono-b font-bold uppercase">STOCK / QTY</label>
          <input data-testid="edit-stock" required type="number" min={0} value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} className="brutal-input w-full mt-1" />
        </div>
        <div>
          <label className="text-xs font-mono-b font-bold uppercase">SIZES (comma sep)</label>
          <input data-testid="edit-sizes" value={f.sizes} onChange={(e) => setF({ ...f, sizes: e.target.value })} className="brutal-input w-full mt-1" placeholder="S, M, L" />
        </div>
        <div>
          <label className="text-xs font-mono-b font-bold uppercase">COLORS (comma sep)</label>
          <input data-testid="edit-colors" value={f.colors} onChange={(e) => setF({ ...f, colors: e.target.value })} className="brutal-input w-full mt-1" placeholder="Black, White" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-mono-b font-bold uppercase">TAGS (comma sep)</label>
          <input data-testid="edit-tags" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className="brutal-input w-full mt-1" />
        </div>
        {isAdmin && (
          <>
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">CATEGORY</label>
              <select data-testid="edit-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="brutal-input w-full mt-1 uppercase">
                {["jackets","tees","jeans","tote-bags","tops","thrifted"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 font-mono-b text-sm">
              <input type="checkbox" data-testid="edit-is-custom" checked={f.is_custom} onChange={(e) => setF({ ...f, is_custom: e.target.checked })} /> Custom-made (uncheck for thrifted)
            </label>
          </>
        )}
        <div className="md:col-span-2">
          <label className="text-xs font-mono-b font-bold uppercase">DESCRIPTION</label>
          <textarea data-testid="edit-description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="brutal-input w-full mt-1" />
        </div>
      </div>

      <div>
        <label className="text-xs font-mono-b font-bold uppercase block mb-2">PHOTOS</label>
        <ImageUpload value={f.images} onChange={(imgs) => setF({ ...f, images: imgs })} max={8} testIdPrefix="edit-photos" />
      </div>

      {(!f.is_custom || isAdmin) && (
        <label className="flex items-start gap-3 border-2 border-black bg-[#F4F4F0] p-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="edit-bargain-enabled"
            checked={f.bargain_enabled}
            onChange={(e) => setF({ ...f, bargain_enabled: e.target.checked })}
            className="mt-1"
          />
          <div>
            <p className="font-mono-b font-bold text-sm uppercase">Allow bargaining</p>
            <p className="font-mono-b text-xs text-black/60 mt-0.5">Only meaningful for marketplace/thrifted items. Turn off and buyers won't see the BARGAIN button.</p>
          </div>
        </label>
      )}

      <div className="flex gap-2 flex-wrap pt-2">
        <button data-testid="edit-submit" disabled={saving} type="submit" className="bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-2 font-mono-b font-bold uppercase disabled:opacity-50">
          {saving ? "SAVING..." : "SAVE CHANGES"}
        </button>
        <button type="button" onClick={onCancel} className="border-2 border-black bg-white px-6 py-2 font-mono-b font-bold uppercase brutal-shadow-sm hover-nudge">
          CANCEL
        </button>
      </div>
    </form>
  );
}
